import React, { useState, useEffect } from 'react';
import { NJButton, NJToggle } from '@engie-group/fluid-design-system-react';
import { InputNumber, Select, Modal, Row, Col, Flex, Space } from 'antd';
import './InsightsPopup.css';
import { ReactComponent as CustomSelectIcon } from './icons/icon-select-dropdown.svg';
import { useReferentials } from './context/ReferentialsContext';
import { maxJobDescriptionLength } from './config';


function InsightsPopup({ isOpen, handleClose, handleValidate, insightsOptions: initialOptions, editJobDescription, jobDescription: jd }) {

    const { referentials } = useReferentials();

    const currencyItems = [
        { label: 'Euro', value: 'k€' },
        { label: 'Dollar', value: 'k$' }
    ];

    const [insightsOptions, setInsightsOptions] = useState(initialOptions || {});
    const [country, setCountry] = useState(insightsOptions.country || []);
    const [gbu, setGbu] = useState(insightsOptions.gbu);
    const [language, setLanguage] = useState(insightsOptions.language);
    const [remote, setRemote] = useState(insightsOptions.remote);
    const [jobFamily, setJobFamily] = useState(insightsOptions.jobFamily);
    const [tone, setTone] = useState(insightsOptions.tone);
    const [seniority, setSeniority] = useState(insightsOptions.seniority);
    const [spc, setSpc] = useState(insightsOptions.spc);
    const [persona, setPersona] = useState(insightsOptions.persona);
    const [woman, setWoman] = useState(insightsOptions.woman || false);
    const [currency, setCurrency] = useState(insightsOptions.currency);
    const [salaryRangeMin, setSalaryRangeMin] = useState(insightsOptions?.SalaryRange?.length > 0 ? insightsOptions?.SalaryRange[0] : undefined);
    const [salaryRangeMax, setSalaryRangeMax] = useState(insightsOptions?.SalaryRange?.length > 1 ? insightsOptions?.SalaryRange[1] : undefined);
    const [jobDescription, setJobDescription] = useState(jd);
    const [inStep2, setInStep2] = useState(false);
    const [charCount, setCharCount] = useState(0);

    useEffect(() => {
        if (isOpen) {
            setInStep2(editJobDescription);
            setInsightsOptions(initialOptions || {});
            setJobDescription(jd);
            setCharCount(jd ? jd.length : 0);
            setCountry(initialOptions.country || []);
            setGbu(initialOptions.gbu);
            setLanguage(initialOptions.language);
            setRemote(initialOptions.remote);
            setJobFamily(initialOptions.jobFamily);
            setTone(initialOptions.tone);
            setSeniority(initialOptions.seniority);
            setSpc(initialOptions.spc);
            setPersona(initialOptions.persona);
            setWoman(initialOptions.woman || false);
            setCurrency(initialOptions.currency);
            setSalaryRangeMin(initialOptions?.SalaryRange?.length > 0 ? initialOptions?.SalaryRange[0] : undefined);
            setSalaryRangeMax(initialOptions?.SalaryRange?.length > 1 ? initialOptions?.SalaryRange[1] : undefined);
        }
    }, [isOpen, initialOptions, jd, editJobDescription]);

    const validate = (e) => {
        handleValidate(
            {
                country,
                gbu,
                language,
                remote,
                jobFamily,
                tone,
                seniority,
                persona,
                currency,
                spc,
                woman,
                salaryRange: [salaryRangeMin, salaryRangeMax],
            },
            jobDescription
        );
    }

    function buildSelectList(prop) {
        if (referentials && referentials[prop]) {
            return [...(referentials[prop] ? referentials[prop].map((item, index) => { return { label: item, value: item } }) : [])]
        }
        return []
    }

    function modalTitle() {
        return !editJobDescription ? (
            <span className='insights-title'>
                <span aria-hidden="true" className="material-icons-outlined insights-title-icon">loupe</span>
                Do you want to add specific insights?
            </span>
        ) : (
            <span className='insights-title'>
                <span aria-hidden="true" className="material-icons-outlined insights-title-icon">edit</span>
                Update Filters
            </span>
        )
    }

    return (

        <Modal
            width={818}
            title={modalTitle()}
            open={isOpen}
            onOk={validate}
            onCancel={handleClose}
            destroyOnClose
            keyboard={false}
            closable={true}
            maskClosable={false} // Prevent closing on outside click
            footer={[
                <Flex justify='flex-end' justifyContent="end" gap={20}>
                    <NJButton
                        label="Cancel"
                        variant="secondary"
                        onClick={handleClose}
                    />
                    {(inStep2 && editJobDescription &&
                        <NJButton
                            label="Next"
                            variant="primary"
                            emphasis="subtle"
                            onClick={() => setInStep2(false)}
                            disabled={!jobDescription} // Disable if job description is empty
                        />)}
                    {(!inStep2 && editJobDescription &&
                        <NJButton
                            label="Previous"
                            variant="primary"
                            emphasis="subtle"
                            onClick={() => setInStep2(true)}
                        />)}
                    {((!inStep2 || !editJobDescription) &&
                        <NJButton
                            label="Generate Ad"
                            variant="ai"
                            icon="engie_ai"
                            onClick={validate}
                        />
                    )}
                </Flex>
            ]}
        >

            <div className={`insights-popup-block ${editJobDescription ? 'insights-popup-fixed-height' : ''}`}>

                {/*(editJobDescription &&
                    <div className='insights-edit-message'>
                        Make the necessary changes and generate the Ad job again
                    </div>
                )*/}

                {(!inStep2 &&
                    <>
                        <Row gutter={[16,16]} className='insights-popup-form-row-title'>
                            <Col span={24} className='insights-popup-form-targeting'>
                                For better targeting, please make sure to fill in all selectors marked with ⟐
                            </Col>
                             <Col span={24} className='insights-popup-form-title'>
                                Localization and job Ad language
                            </Col>   
                        </Row>

                        <Row gutter={16} className='insights-popup-form-row-input'>
                            <Col span={12}>
                                <Select style={{ width: '100%' }}
                                    value={country}
                                    placeholder="Countries ⟐"
                                    mode="multiple"
                                    allowClear
                                    showSearch
                                    onChange={(value) => { setCountry(value) }}
                                    options={buildSelectList('country')}
                                    suffixIcon={<CustomSelectIcon />}
                                    size="large"
                                />
                            </Col>

                            <Col span={12}>

                                <Select style={{ width: '100%' }}
                                    value={gbu}
                                    allowClear
                                    placeholder="ENGIE GBU"
                                    showSearch
                                    onChange={(value) => { setGbu(value) }}
                                    options={buildSelectList('globalBusinessUnit')}
                                    suffixIcon={<CustomSelectIcon />}
                                />
                            </Col>
                        </Row>
                        <Row gutter={16} className='insights-popup-form-row-input'>
                            <Col span={12}>
                                <Select style={{ width: '100%' }}
                                    value={language}
                                    allowClear
                                    placeholder="Job ad language"
                                    showSearch
                                    onChange={(value) => { setLanguage(value) }}
                                    options={buildSelectList('languages')}
                                    suffixIcon={<CustomSelectIcon />}
                                />

                            </Col>
                            <Col span={12}>
                                <Select style={{ width: '100%' }}
                                    value={remote}
                                    allowClear
                                    placeholder="Work arrangement"
                                    showSearch
                                    onChange={(value) => { setRemote(value) }}
                                    options={buildSelectList('remote')}
                                    suffixIcon={<CustomSelectIcon />}
                                />
                            </Col>

                        </Row>

                        <Row gutter={16} className='insights-popup-form-row-title'>
                            <Col span={24} className='insights-popup-form-title'>
                                Job request
                            </Col>
                        </Row>

                        <Row gutter={16} className='insights-popup-form-row-input'>
                            <Col span={12}>
                                <NJToggle
                                    className='insights-popup-toggle'
                                    id="toggleReachWoman"
                                    label="Reach woman recruitment ⟐"
                                    values={{
                                        off: 'Yes',
                                        on: 'No'
                                    }}
                                    checked={woman}
                                    onChange={(event, checked) => { setWoman(checked) }}
                                />
                            </Col>
                        </Row>

                        <Row gutter={16} className='insights-popup-form-row-input'>
                            <Col span={12}>
                                <Select style={{ width: '100%' }}
                                    value={jobFamily}
                                    allowClear
                                    placeholder="Job Family ⟐"
                                    showSearch
                                    onChange={(value) => { setJobFamily(value) }}
                                    options={buildSelectList('jobFamily')}
                                    suffixIcon={<CustomSelectIcon />}
                                />
                            </Col>
                            <Col span={12}>
                                <Select style={{ width: '100%' }}
                                    value={seniority}
                                    allowClear
                                    placeholder="Seniority ⟐"
                                    showSearch
                                    onChange={(value) => { setSeniority(value) }}
                                    options={buildSelectList('seniorityLevel')}
                                    suffixIcon={<CustomSelectIcon />}
                                />

                            </Col>
                        </Row>

                        <Row gutter={16} className='insights-popup-form-row-input'>
                            <Col span={12}>
                                <Select style={{ width: '100%' }}
                                    value={spc}
                                    allowClear
                                    placeholder="Global Employee Level ⟐"
                                    showSearch
                                    onChange={(value) => { setSpc(value) }}
                                    options={buildSelectList('socioProfessionalCategory')}
                                    suffixIcon={<CustomSelectIcon />}
                                />

                            </Col>
                            <Col span={12}>
                                {/*
                                <Select style={{ width: '100%' }}
                                    value={tone}
                                    allowClear
                                    placeholder="Tone"
                                    showSearch
                                    onChange={(value) => { setTone(value) }}
                                    options={buildSelectList('tone')}
                                    suffixIcon={<CustomSelectIcon />}
                                    disabled={false}
                                />
                                */}
                            </Col>
                        </Row>


                        <Row gutter={16} className='insights-popup-form-row-title'>
                            <Col span={24} className='insights-popup-form-title'>
                                Pay transparency
                            </Col>
                        </Row>

                        <Row gutter={16} className='insights-popup-form-row-input'>
                            <Col span={6}>
                                <Select style={{ width: '100%' }}
                                    value={currency}
                                    allowClear
                                    placeholder="Currency"
                                    showSearch
                                    onChange={(value) => { setCurrency(value) }}
                                    options={currencyItems}
                                    suffixIcon={<CustomSelectIcon />}
                                />

                            </Col>
                            <Col span={6}>
                                <Space size={16}>
                                    <InputNumber
                                        disabled={!currency}
                                        placeholder='Min (k)'
                                        size='large'
                                        min={1} max={400}
                                        defaultValue={salaryRangeMin}
                                        onChange={(value) => { setSalaryRangeMin(value) }}
                                        changeOnWheel />

                                    <InputNumber
                                        disabled={!currency}
                                        placeholder='Max (k)'
                                        size='large'
                                        min={1} max={400}
                                        defaultValue={salaryRangeMax}
                                        onChange={(value) => { setSalaryRangeMax(value) }}
                                        changeOnWheel />
                                </Space>
                            </Col>
                        </Row>
                    </>
                )}

                {(inStep2 &&
                    <>

                        <Row gutter={16} className='insights-popup-form-row-title'>
                            <Col span={24} className='insights-popup-form-title'>
                                Initial description
                            </Col>
                        </Row>

                        <Row gutter={16} className='insights-popup-form-row-input'>
                            <Col span={24}>

                                <textarea
                                    spellCheck="false"
                                    className={`insights-popup-job ${!jobDescription ? 'error' : ''}`} // Add error class if invalid
                                    value={jobDescription}
                                    onChange={(e) => {
                                        setJobDescription(e.target.value)
                                        setCharCount(e.target.value.length);
                                    }}
                                    placeholder={referentials?.jobDescriptionPlaceholder || ''}
                                    rows="10"
                                    maxLength={maxJobDescriptionLength}
                                />
                                <div className={`insights-char-counter ${charCount > (maxJobDescriptionLength * .9) ? (charCount >= maxJobDescriptionLength ? 'at-limit' : 'near-limit') : ''
                                    }`}>
                                    {charCount}/{maxJobDescriptionLength} characters
                                </div>
                            </Col>
                        </Row>
                    </>)}
            </div>
        </Modal>

    )
}

export default InsightsPopup;